/**
 * Wrapper leggero su globe.gl. Lazy-loaded.
 *
 * Nota importante: non passiamo a globe.gl tutti i paesi del GeoJSON.
 * Manteniamo la texture terrestre come base e renderizziamo come poligoni solo
 * i paesi già tentati/rivelati. È lo stesso principio prestazionale osservato
 * su Globle: pochi mesh dinamici invece di una mesh per ogni paese.
 */

import { assetUrl } from '../../ui/assets';
import type { Country } from './logic';

interface CountryFeature {
  type: 'Feature';
  properties: { ISO_A3?: string; ADM0_A3?: string; ADMIN?: string };
  geometry: unknown;
}

interface RenderedCountryFeature extends CountryFeature {
  properties: CountryFeature['properties'] & {
    renderColor: string;
    revealed?: boolean;
  };
}

export interface GlobeView3D {
  /** Evidenzia un paese. Se non ha geometria, usa fallbackCentroid [lon,lat] per piazzare un marker. */
  highlight(iso3: string, colorHsl: string, fallbackCentroid?: [number, number]): void;
  /** Rivela il paese misterioso (verde lampeggiante) e lo focalizza. */
  reveal(country: Country, isPolygonInGeo: boolean): void;
  focus(country: Country): void;
  destroy(): void;
}

interface MarkerPoint {
  lat: number;
  lng: number;
  color: string;
  size: number;
}

const TARGET_COLOR = '#22c55e'; // verde Globle-style per il target rivelato

function isLowPowerDevice(): boolean {
  const nav = navigator as Navigator & { deviceMemory?: number };
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (nav.deviceMemory !== undefined && nav.deviceMemory <= 4) ||
    navigator.hardwareConcurrency <= 4;
}

export async function createGlobe(args: {
  container: HTMLElement;
  geoFeatures: CountryFeature[];
}): Promise<GlobeView3D> {
  const { default: Globe } = await import('globe.gl');

  const lowPower = isLowPowerDevice();
  const featureByIso = new Map<string, CountryFeature>();

  const getIso = (f: CountryFeature): string =>
    f.properties.ISO_A3 ?? f.properties.ADM0_A3 ?? '';

  for (const feature of args.geoFeatures) {
    const iso = getIso(feature);
    if (iso) featureByIso.set(iso, feature);
  }

  const rendered = new Map<string, RenderedCountryFeature>();

  function toRenderedFeature(feature: CountryFeature, color: string, revealed = false): RenderedCountryFeature {
    return {
      ...feature,
      properties: {
        ...feature.properties,
        renderColor: color,
        revealed,
      },
    };
  }

  function renderedFeatures(): RenderedCountryFeature[] {
    return [...rendered.values()];
  }

  const inst = new Globe(args.container, { rendererConfig: { antialias: !lowPower } })
    .backgroundColor('rgba(0,0,0,0)')
    .showGlobe(true)
    .showAtmosphere(!lowPower)
    .atmosphereColor('#38bdf8')
    .atmosphereAltitude(lowPower ? 0.08 : 0.18)
    .globeImageUrl(assetUrl('images/earth-blue-marble-1024.jpg'))
    .polygonsData([])
    .polygonAltitude((d: object) => ((d as RenderedCountryFeature).properties.revealed ? 0.02 : 0.012))
    .polygonCapColor((d: object) => (d as RenderedCountryFeature).properties.renderColor)
    .polygonSideColor((d: object) => ((d as RenderedCountryFeature).properties.revealed ? 'rgba(34,197,94,0.45)' : 'rgba(0,0,0,0.35)'))
    .polygonStrokeColor((d: object) => ((d as RenderedCountryFeature).properties.revealed ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.55)'))
    .polygonLabel(() => '');

  // Markers per paesi senza geometria (micro-stati) o per il target se non ha poligono.
  const markers: MarkerPoint[] = [];
  inst
    .pointsData(markers)
    .pointLat((d: object) => (d as MarkerPoint).lat)
    .pointLng((d: object) => (d as MarkerPoint).lng)
    .pointColor((d: object) => (d as MarkerPoint).color)
    .pointAltitude(0.02)
    .pointRadius((d: object) => (d as MarkerPoint).size)
    .pointLabel(() => '');

  const renderer = inst.renderer?.() as { setPixelRatio?: (ratio: number) => void } | undefined;
  renderer?.setPixelRatio?.(Math.min(window.devicePixelRatio || 1, lowPower ? 1 : 1.5));

  function refreshPolygons(): void {
    inst.polygonsData(renderedFeatures());
  }

  const ro = new ResizeObserver(() => {
    const side = Math.min(args.container.clientWidth, args.container.clientHeight);
    inst.width(side);
    inst.height(side);
  });
  ro.observe(args.container);
  const initSide = Math.min(args.container.clientWidth, args.container.clientHeight || args.container.clientWidth);
  inst.width(initSide);
  inst.height(initSide || 320);

  // Globo fermo: nessuna rotazione automatica.
  const controls = inst.controls() as { autoRotate: boolean; zoomSpeed?: number; enableDamping?: boolean };
  controls.autoRotate = false;
  controls.zoomSpeed = lowPower ? 0.7 : 1;
  controls.enableDamping = !lowPower;

  return {
    highlight(iso3, colorHsl, fallbackCentroid) {
      const feature = featureByIso.get(iso3);
      if (feature) {
        rendered.set(iso3, toRenderedFeature(feature, colorHsl));
        refreshPolygons();
      } else if (fallbackCentroid) {
        const [lon, lat] = fallbackCentroid;
        if (!markers.some((m) => Math.abs(m.lat - lat) < 1e-6 && Math.abs(m.lng - lon) < 1e-6)) {
          markers.push({ lat, lng: lon, color: colorHsl, size: 0.45 });
          inst.pointsData([...markers]);
        }
      }
    },
    reveal(country, isPolygonInGeo) {
      const feature = featureByIso.get(country.iso3);
      if (isPolygonInGeo && feature) {
        rendered.set(country.iso3, toRenderedFeature(feature, TARGET_COLOR, true));
        refreshPolygons();
      } else {
        const [lon, lat] = country.centroid;
        markers.push({ lat, lng: lon, color: TARGET_COLOR, size: 0.9 });
        inst.pointsData([...markers]);
      }
      const [lon, lat] = country.centroid;
      inst.pointOfView({ lat, lng: lon, altitude: 1.6 }, 1200);
    },
    focus(country) {
      const [lon, lat] = country.centroid;
      inst.pointOfView({ lat, lng: lon, altitude: 1.8 }, 800);
    },
    destroy() {
      ro.disconnect();
      try {
        inst._destructor?.();
      } catch {
        /* noop */
      }
      args.container.innerHTML = '';
    },
  };
}
